import type { ReactNode } from 'react'

import { useBrokerLogo } from '@/components/ui/broker-logo'
import { cn } from '@/lib/utils'

/**
 * Every size the Broker Analysis mock draws a mark at (COMPARE-bo §4), named by its mock pixel
 * size, plus the three t-shirt keys earlier callers use (`sm` 24, `md` 40, `lg` 48):
 *
 *   18  the rail's Top rated rows — built 20, since 18 is a half step off the 4-grid
 *   24  table and bar rows        28  the picker menu      32  a Large trades leader
 *   36  a Compare pick card
 *   40  older table rows (`md`)   44  a raised mark        48  `lg`
 *   60  a broker's page header
 *
 * The figure steps up with the disc: 12/600 to 28, 14/600 to 48, 16/600 at 60.
 */
const SIZES = {
  18: 'size-5 text-h-xs',
  24: 'size-6 text-h-xs',
  28: 'size-7 text-h-xs',
  32: 'size-8 text-h-s',
  36: 'size-9 text-h-s',
  40: 'size-10 text-h-s',
  44: 'size-11 text-h-s',
  48: 'size-12 text-h-s',
  60: 'size-15 text-h-m',
  sm: 'size-6 text-h-xs',
  md: 'size-10 text-h-s',
  lg: 'size-12 text-h-s',
} as const

export type BrokerMarkSize = keyof typeof SIZES

interface BrokerMarkProps {
  /** The NEPSE broker number, e.g. 33 for "Broker 33" — also the sr-only name. */
  brokerId: number | string
  /**
   * The house's logo, absolute (`brokerService` resolves the wire's relative path). Missing,
   * relative or failing to load, the mark falls back to the numbered badge. Only the broker
   * detail read carries one today; list rows pass it once their wire does (asks/broker-logos.md).
   */
  logoUrl?: string | null | undefined
  size?: BrokerMarkSize
  className?: string
}

interface BrokerMarkFaceProps {
  brokerId: number | string
  /** The resolved logo (`useBrokerLogo`), or null for the badge. */
  src: string | null
  onError: () => void
  size: BrokerMarkSize
  className?: string | undefined
}

/**
 * The two faces of a broker mark, as the mock draws them: the logo in a round frame on the raised
 * fill with a subtle hairline drawn just inside its edge, or the code badge — the inset fill under
 * a subtle inner hairline, the number in 600 secondary. The rail's 18px badge has no hairline, as
 * the mock's `bra-rail__mark` has none. Same box either way, so a logo failing to load never moves
 * the row.
 */
function BrokerMarkFace({ brokerId, src, onError, size, className }: BrokerMarkFaceProps) {
  const id = String(brokerId)
  if (src !== null) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 overflow-hidden rounded-full bg-popover outline -outline-offset-1 outline-border-subtle',
          SIZES[size],
          className,
        )}
      >
        <img src={src} alt="" onError={onError} className="size-full object-cover" />
        <span className="sr-only">Broker {id}</span>
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground',
        size === 18 ? null : 'inset-ring inset-ring-border-subtle',
        SIZES[size],
        className,
      )}
    >
      <span aria-hidden="true">{id}</span>
      <span className="sr-only">Broker {id}</span>
    </span>
  )
}

/**
 * The circular broker identifier beside a floorsheet, depth or broker row: the house's logo when
 * it has one, else its NEPSE number on a badge. A broker's identity is its NUMBER, never a letter
 * mark: two houses numbered "07" and "01" would both draw a lone "0".
 *
 * Beside a printed name, use `BrokerIdentity`, which also applies the trailing-code rule.
 */
export default function BrokerMark({ brokerId, logoUrl, size = 'md', className }: BrokerMarkProps) {
  const logo = useBrokerLogo(String(brokerId), logoUrl)
  return (
    <BrokerMarkFace
      brokerId={brokerId}
      src={logo.src}
      onError={logo.onError}
      size={size}
      className={className}
    />
  )
}

interface BrokerIdentityProps {
  code: string
  /** What prints: `brokerShortName` in lists, `brokerFullName` in a header, picker or pick card. */
  name: ReactNode
  logoUrl?: string | null | undefined
  size?: BrokerMarkSize
  /** The name's type and ink; default 12/400 primary, the mock's bar-row name. */
  nameClassName?: string
  className?: string
}

/**
 * A broker mark with its name beside it, and the mock's trailing-code rule: a LOGO says nothing
 * about which number the house is, so the code follows the name in 12 tertiary; a BADGE already
 * prints the number, so nothing follows. Decided off the face actually drawn, so a logo that fails
 * to load brings the badge and drops the trailing code with it.
 *
 * The name carries `data-row-name`, so a DataTable link row underlines it on hover.
 */
export function BrokerIdentity({
  code,
  name,
  logoUrl,
  size = 24,
  nameClassName,
  className,
}: BrokerIdentityProps) {
  const logo = useBrokerLogo(code, logoUrl)
  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <BrokerMarkFace brokerId={code} src={logo.src} onError={logo.onError} size={size} />
      <span data-row-name="" className={cn('truncate text-body-xs text-foreground', nameClassName)}>
        {name}
      </span>
      {logo.src === null ? null : (
        <span className="shrink-0 text-body-xs text-muted-foreground-subtle tnum">{code}</span>
      )}
    </span>
  )
}
